import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat } from 'app/lib/date';

import type { HistoryMonth } from './types';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Theme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface CalendarCellProps {
  month: HistoryMonth;
}

function getCalendarCellViewModel(month: HistoryMonth) {
  const [year, monthNumber] = month.period.split('-').map(Number);
  const date = new Date(year ?? 1970, (monthNumber ?? 1) - 1, 1);
  const now = new Date();
  const isCurrent = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  const isFuture = date > now;
  const isFreeze = month.isFreeze;
  const achieved =
    month.numUsersTarget !== 0 && month.numLoans >= Math.round(month.numUsersTarget * month.targetMultiplier);
  const bg: keyof Theme['colors'] = achieved
    ? 'surface.brand.secondary-3.default'
    : isFreeze
      ? 'surface.info.subtle'
      : isCurrent || isFuture
        ? 'white'
        : 'surface.secondary-neutral';
  const borderColor: keyof Theme['colors'] = isFreeze
    ? 'surface.info.subtle'
    : isCurrent && achieved
      ? 'surface.default'
      : isCurrent
        ? 'surface.brand.default'
        : isFuture
          ? 'surface.secondary-neutral'
          : 'surface.disabled';
  const valueMain: keyof Theme['colors'] = achieved ? 'white' : isFreeze ? 'text.secondary' : 'text.primary';
  const valueDenom: keyof Theme['colors'] = achieved ? 'white' : isFreeze ? 'icon.tertiary' : 'text.primary';
  const labelBg: keyof Theme['colors'] = isFreeze
    ? 'surface.disabled'
    : isCurrent && achieved
      ? 'surface.brand.secondary-3.default'
      : isCurrent
        ? 'surface.brand.default'
        : 'surface.disabled';
  const rawMonthLabel = localeAwareFormat(date, 'MMM').replace('.', '').slice(0, 4);

  return {
    achieved,
    bg,
    borderColor,
    isCurrent,
    isFreeze,
    isFuture,
    labelBg,
    monthLabel: `${rawMonthLabel.charAt(0).toUpperCase()}${rawMonthLabel.slice(1)}.`,
    valueDenom,
    valueMain,
  };
}

export const CalendarCell = function CalendarCell({ month }: CalendarCellProps) {
  const { achieved, bg, borderColor, isCurrent, isFreeze, isFuture, labelBg, monthLabel, valueDenom, valueMain } =
    getCalendarCellViewModel(month);

  return (
    <Column alignItems="center" width="14%">
      <Row
        backgroundColor={labelBg}
        justifyContent="center"
        position="relative"
        width="100%"
        minHeight={50}
        alignItems="center"
        bottom={-15}
        borderRadius="radius.sm"
      >
        <Text
          color={isFreeze ? 'text.secondary' : 'text.primary'}
          fontSize={12}
          textAlign="center"
          style={{ position: 'relative', bottom: 5 }}
        >
          {monthLabel}
        </Text>
      </Row>

      <Column
        alignItems="center"
        backgroundColor={bg}
        borderColor={borderColor}
        borderRadius="radius.md"
        borderWidth={isFreeze ? 0 : isCurrent || isFuture ? 1 : 0}
        height={56}
        justifyContent="center"
        paddingVertical="spacing.general.x1"
        width="100%"
      >
        {isFuture ? (
          <Text color="text.tertiary" fontSize={15}>
            {Math.round(month.numUsersTarget * month.targetMultiplier)}
          </Text>
        ) : (
          <>
            <Row alignItems="center" height={16} justifyContent="center">
              {achieved ? (
                <Text fontSize={14} lineHeight={16} textAlign="center">
                  🔥
                </Text>
              ) : isFreeze ? (
                <Text fontSize={14} lineHeight={16} textAlign="center">
                  🧊
                </Text>
              ) : !isCurrent ? (
                <IconX strokeWidth={ICON_STROKE_WIDTH} size={14} color={theme.colors['text.primary']} />
              ) : null}
            </Row>

            <Text color={valueMain} fontSize={15} fontWeight="bold">
              {month.numLoans}
            </Text>
            <Text color={valueDenom} fontSize={11}>
              /{Math.round(month.numUsersTarget * month.targetMultiplier)}
            </Text>
          </>
        )}
      </Column>
    </Column>
  );
};
